import './MemberAnketaQuestions.scss';

import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import AnimatedRedirect from '../../components/AnimatedRedirect';
import AnketaAnswerCheckboxes from './AnketaAnswerCheckboxes';
import AnketaAnswerDraggable from './AnketaAnswerDraggable';
import AnketaAnswerForm from './AnketaAnswerForm';
import AnketaAnswerRadio from './AnketaAnswerRadio';
import AnketaDescriptionModal from '../../components/AnketaSharedComponents/AnketaDescriptionModal';
import AnketaQuestion from '../../components/AnketaSharedComponents/AnketaQuestion';
import ErrorInfiniteRetry from '../../components/ErrorInfiniteRetry';
import MemberAnketaHeader from '../../components/AnketaSharedComponents/MemberAnketaHeader';
import MemberAnketaProgression from '../../components/AnketaSharedComponents/MemberAnketaProgression';
import MemberAnketaSubquestionsProgression from '../../components/AnketaSharedComponents/MemberAnketaSubquestionsProgression';
import NoInterviewModal from './NoInterviewModal';
import NoTimeLeftModal from './NoTimeLeftModal';
import PageLoader from '../../components/PageLoader';
import axios from 'axios';
import { backendURL } from '../../App';
import { motion } from 'framer-motion';
import { queryCache } from 'react-query';
import { standardPageVariants } from '../../animationsVariants/StandardPageVariants';
import useClientAnketaData from '../../hooks/useClientAnketaData';
import useUserData from '../../hooks/useUserData';

export const MemberAnketaQuestions: React.FC<{}> = () => {
  const history = useHistory();
  const [submitError, setSubmitError] = useState<any | null>(null);
  const { anketaID } = useParams<{ anketaID: string }>();
  const { data: anketaData, status: anketaDataLoadingStatus, error: anketaError } = useClientAnketaData(anketaID);
  const { data: userData, status: userDataLoadingStatus, error: userError } = useUserData();
  const [isAnketaDescriptionModal, setIsAnketaDescriptionModal] = useState<boolean>(false);
  const [isNoInterviewModal, setIsNoInterviewModal] = useState<boolean>(false); // * initial state true + anketaData.questions_completed allows not to trigger endless rerender

  const [memberStartDateSet, setMemberStartDateSet] = useState(false);

  // POST /api/member-start-date/ один раз при заходе на первый вопрос, если start ещё не записан.
  useEffect(() => {
    if (
      anketaData?.question_number === 1 &&
      !anketaData?.member_start_date &&
      !memberStartDateSet &&
      anketaData?.time_for_anketa !== null &&
      anketaData?.time_for_anketa !== undefined
    ) {
      setMemberStartDateSet(true);
      axios({
        method: 'post',
        url: `${backendURL}member-start-date/`,
        headers: {
          Authorization: `Token ${localStorage.getItem('skillsSurveyUserToken')}`,
        },
      }).then(() => {
        // Один refetch, чтобы получить свежий time_left от сервера.
        // Это НЕ периодический polling — мотивация коммита 4c09ee9 сохраняется.
        queryCache.invalidateQueries('clientAnketaData');
      }).catch(() => {
        setMemberStartDateSet(false); // Разрешаем повторную попытку при ошибке
      });
    }
  }, [anketaData?.question_number, anketaData?.member_start_date, anketaData?.time_for_anketa, memberStartDateSet]);

  // Запоминаем момент получения time_left от сервера.
  // Зависимость — anketaData.time_left: при одинаковом значении эффект не
  // срабатывает (тикер не сбивается); при изменении (после ответа +
  // invalidateQueries) receivedAt пересинхронизируется.
  const [receivedAt, setReceivedAt] = useState<number | null>(null);
  useEffect(() => {
    if (anketaData?.time_left !== undefined && anketaData?.time_left !== null) {
      setReceivedAt(Date.now());
    }
  }, [anketaData?.time_left]);

  // Локальный тикер: чистая арифметика на числах, никаких new Date(ISO) →
  // нет TZ-зависимости от браузера клиента.
  const [computedTimeLeft, setComputedTimeLeft] = useState<number | null>(null);
  useEffect(() => {
    if (
      !anketaData?.time_for_anketa ||
      receivedAt === null ||
      anketaData?.time_left === null ||
      anketaData?.time_left === undefined
    ) {
      setComputedTimeLeft(null);
      return;
    }

    const updateTimeLeft = () => {
      const elapsedMinutes = Math.floor((Date.now() - receivedAt) / 60000);
      setComputedTimeLeft(Math.max(0, anketaData.time_left - elapsedMinutes));
    };

    updateTimeLeft();
    const interval = setInterval(updateTimeLeft, 1000);

    return () => clearInterval(interval);
  }, [anketaData?.time_for_anketa, anketaData?.time_left, receivedAt]);

  // Приоритет источников ошибки: submit > anketa > user. submitError содержит axios-объект,
  // anketaError/userError приходят из react-query (тоже axios-shape). ErrorInfiniteRetry
  // сам делит на network/server-ветки по наличию error.response.
  const displayError = submitError || anketaError || userError;

  // Error-приоритет: если ЛЮБОЙ из запросов уже завершился ошибкой — показываем
  // ErrorInfiniteRetry с реальным error (даже если второй запрос ещё в loading).
  // Иначе пока хотя бы один в loading — крутим лоадер.
  // Раньше при failureCount>0 показывали ErrorInfiniteRetry без error → fallback ветка
  // отрисовывала «технические проблемы» даже при потере связи (BUG-1). Теперь дожидаемся
  // реального status='error' (react-query: retry=3 + backoff ~3.5с) и пробрасываем
  // настоящий axios-объект в компонент → ветки делятся корректно.
  if (anketaDataLoadingStatus === 'error' || userDataLoadingStatus === 'error' || submitError !== null) {
    return <ErrorInfiniteRetry error={displayError} resetStateErrors={() => setSubmitError(null)} />;
  }

  if (anketaDataLoadingStatus === 'loading' || userDataLoadingStatus === 'loading') {
    return <PageLoader />;
  }

  if (
    (anketaData.status === 1 && anketaData.questions_completed && !anketaData.anketa.interview_needless) ||
    (anketaData.status !== 0 && anketaData.status !== 1 && !anketaData.anketa.interview_needless)
  ) {
    return <AnimatedRedirect redirectTo={`/client/anketa/${anketaID}/call_schedulling/`} />;
  }

  if (
    (anketaData.status === 1 && anketaData.questions_completed && anketaData.anketa.interview_needless && !isNoInterviewModal) ||
    (anketaData.status !== 0 && anketaData.status !== 1 && anketaData.anketa.interview_needless)
  ) {
    return <AnimatedRedirect redirectTo='/client/anketa/complete/' />;
  }

  // Используем вычисленное время или время с бэкенда
  const anketaDataWithTime = {
    ...anketaData,
    time_left: computedTimeLeft !== null ? computedTimeLeft : anketaData?.time_left,
  };

  return (
    <motion.main className='memberAnketaQuestions' variants={standardPageVariants} initial='initial' animate='animate' exit='exit'>
      <MemberAnketaHeader userData={userData.user} anketaData={anketaDataWithTime} />
      <MemberAnketaProgression questionsCount={anketaData.questions_count} currentQuestionNumber={anketaData.question_number} />
      {anketaData.subquestion && (
        <MemberAnketaSubquestionsProgression subquestionsCount={anketaData.question.subquestions.length} currentSubquestionNumber={anketaData.subquestion_number} />
      )}
      <AnketaQuestion
        questionData={anketaData.subquestion ? { text: anketaData.question.text, question_text: anketaData.subquestion.text } : anketaData.question}
        toggleModal={anketaData.anketa.description ? () => setIsAnketaDescriptionModal(true) : undefined}
      />
      {anketaData.subquestion ? (
        <>
          {anketaData.subquestion.sq_type === 0 && (
            <AnketaAnswerRadio
              anketaData={anketaData}
              setIsNoInterviewModal={() => setIsNoInterviewModal(true)}
              setErrorDuringSubmit={(error: any) => setSubmitError(error || { message: 'Unknown submit error' })}
            />
          )}
          {anketaData.subquestion.sq_type === 1 && (
            <AnketaAnswerCheckboxes
              anketaData={anketaData}
              setIsNoInterviewModal={() => setIsNoInterviewModal(true)}
              setErrorDuringSubmit={(error: any) => setSubmitError(error || { message: 'Unknown submit error' })}
            />
          )}
          {anketaData.subquestion.sq_type === 2 && (
            <AnketaAnswerDraggable
              anketaData={anketaData}
              setIsNoInterviewModal={() => setIsNoInterviewModal(true)}
              setErrorDuringSubmit={(error: any) => setSubmitError(error || { message: 'Unknown submit error' })}
            />
          )}
        </>
      ) : (
        <AnketaAnswerForm anketaData={anketaData} setIsNoInterviewModal={() => setIsNoInterviewModal(true)} />
      )}
      {isAnketaDescriptionModal && <AnketaDescriptionModal onClose={() => setIsAnketaDescriptionModal(false)} description={anketaData.anketa.description} />}
      {isNoInterviewModal && (
        <NoInterviewModal
          onClose={() => {
            queryCache.invalidateQueries('clientAnketaData');
            setIsNoInterviewModal(false);
          }}
        />
      )}
      {(computedTimeLeft !== null ? computedTimeLeft : anketaData?.time_left) === 0 && (
        <NoTimeLeftModal
          onClose={() => {
            queryCache.invalidateQueries('clientAnketaData');
            history.push('/client/anketa/complete/');
          }}
        />
      )}
    </motion.main>
  );
};
